import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react'
import { getBrazeSDK, initBrazeCore } from '../../Config/Braze/braze.config'
import { useBrazePageTracking } from '../../Hooks'
import { defaultInAppMessageHandler, subscribeToInAppMessages, type BrazeMessage } from '../../Utils/inAppMessaging'
import { isDevEnvironment } from '../../Utils/Window/windowHelpers'
import { normalizeError } from '../../Utils/errors/errorHelpers'
import { logEvent } from '../../Utils/analytics/firAnalytics'
import { ANALYTICS_TAGS } from '../../Constants/analytics'
import { AuthContext } from '../../Api/firebase/authContext'
import { AppError } from '../../Utils/errors/errorHelpers'

type BrazeContextType = {
  brazeInitialized: boolean
  brazeError: AppError | null
}

export const BrazeContext = createContext<BrazeContextType | null>(null)

export const useBraze = () => {
  const context = useContext(BrazeContext)
  if (!context) {
    throw new Error('useBraze must be used within a BrazeProvider')
  }
  return context
}

type BrazeProviderProps = {
  children: React.ReactNode
}

export const BrazeProvider: React.FC<BrazeProviderProps> = ({ children }) => {
  const [brazeInitialized, setBrazeInitialized] = useState<boolean>(false)
  const [brazeError, setBrazeError] = useState<AppError | null>(null)
  const auth = useContext(AuthContext)!
  const { authLoading, userId } = auth
  const [pendingMessage, setPendingMessage] = useState<BrazeMessage | null>(null)
  const subscriptionIdRef = useRef<string | undefined>(undefined)
  const lastUserIdRef = useRef<string | undefined>(undefined)
  const { location } = useBrazePageTracking(brazeInitialized)

  // Stores messages as pending
  const handleInAppMessage = useCallback((message: BrazeMessage) => {
    if (isDevEnvironment()) {
      console.log('[Braze] Storing message as pending:', message)
    }
    setPendingMessage(message)
  }, [])

  const startBraze = useCallback(async () => {
    if (isDevEnvironment()) {
      console.log('[Braze] Starting Braze')
    }
    if (authLoading) return

    try {
      const braze = await getBrazeSDK()
      if (!braze) {
        throw new Error('Failed to load Braze SDK')
      }

      // Pass user ID if authenticated, undefined if anonymous
      const uid = userId ?? undefined

      if (isDevEnvironment()) {
        console.log('[Braze] Initializing with userId:', uid || 'anonymous')
      }

      const success = await initBrazeCore(uid, preSessionSetup)
      if (!success) {
        throw new Error('Failed to initialize Braze core')
      }

      // Track the user used for the current Braze session to avoid duplicate inits
      lastUserIdRef.current = uid

      setBrazeInitialized(true)
      setBrazeError(null)

      if (isDevEnvironment()) {
        console.log(
          '[Braze] Successfully initialized with user:',
          uid ? 'authenticated' : 'anonymous'
        )
      }
    } catch (error) {
      const normalizedError = normalizeError(error)
      console.error('[Braze] Initialization failed:', normalizedError)

      logEvent(ANALYTICS_TAGS.braze_provider_initialization_failed, {
        provider: 'braze',
        error: normalizedError.message,
        hasUserId: !!userId,
        product: 'stats',
      })

      setBrazeError(normalizedError)
      setBrazeInitialized(false)
    }
  }, [authLoading])

  // Pre-session setup, subscribe, don't auto-show
  const preSessionSetup = (braze: any) => {
    // Cleanup any existing subscription
    if (subscriptionIdRef.current && typeof braze?.removeSubscription === 'function') {
      try {
        braze.removeSubscription(subscriptionIdRef.current)
        if (isDevEnvironment()) {
          console.log('[Braze] Cleaned up existing subscription')
        }
      } catch (cleanupError) {
        console.warn('[Braze] Error cleaning up existing subscription:', cleanupError)
      }
      subscriptionIdRef.current = undefined
    }

    subscriptionIdRef.current = subscribeToInAppMessages(braze, handleInAppMessage)

    if (isDevEnvironment()) {
      console.log(
        '[Braze] Pre-session setup complete, subscription ID:',
        subscriptionIdRef.current
      )
    }
  }

  useEffect(() => {
    // Wait for auth before initializing Braze
    if (authLoading) return

    // Initial initialization or user change
    const shouldInitialize = !brazeInitialized || userId !== lastUserIdRef.current

    if (shouldInitialize) {
      if (isDevEnvironment()) {
        const isInitialLoad = !brazeInitialized
        console.log(
          `[Braze] ${isInitialLoad ? 'Initial' : 'User change'} initialization:`,
          {
            hasUserId: !!userId,
            previousUserId: lastUserIdRef.current,
            currentUserId: userId,
          }
        )
      }
      startBraze()
    }
  }, [authLoading, userId, brazeInitialized, startBraze])

  const showPending = useCallback(async () => {
    if (!brazeInitialized || !pendingMessage || !location?.brazePageName) {
      if (isDevEnvironment()) {
        console.log('[Braze] Cannot show pending message:', {
          brazeInitialized,
          hasPendingMessage: !!pendingMessage,
          hasPageName: !!location?.brazePageName,
          brazePageName: location?.brazePageName,
          pageName: location?.pageName,
        })
      }
      return
    }

    if (isDevEnvironment()) {
      console.log('[Braze] Attempting to show pending message:', {
        messageLocation: pendingMessage.extras?.location,
        currentPageName: location.pageName,
        currentBrazePageName: location.brazePageName,
      })
    }

    const messageShown = await defaultInAppMessageHandler(
      pendingMessage,
      location.brazePageName
    )

    if (messageShown) {
      if (isDevEnvironment()) {
        console.log('[Braze] Successfully showed pending message, clearing it')
      }
      setPendingMessage(null)
    }
  }, [brazeInitialized, pendingMessage, location?.brazePageName])

  useEffect(() => {
    // After session start, try showing pending messages
    void showPending()
  }, [showPending])

  useEffect(() => {
    return () => {
      const cleanup = async () => {
        if (subscriptionIdRef.current) {
          try {
            const braze = await getBrazeSDK()
            if (
              braze &&
              subscriptionIdRef?.current &&
              typeof braze?.removeSubscription === 'function'
            ) {
              braze.removeSubscription(subscriptionIdRef.current)
              if (isDevEnvironment()) {
                console.log('[Braze] Successfully removed IAM subscription on unmount')
              }
            }
          } catch (e) {
            console.warn('[Braze] Failed to remove IAM subscription on unmount:', e)
            logEvent(ANALYTICS_TAGS.braze_subscription_cleanup_failed, {
              provider: 'braze',
              error: normalizeError(e)?.message,
              product: 'stats',
            })
          } finally {
            subscriptionIdRef.current = undefined
          }
        }
      }
      void cleanup()
    }
  }, [])

  const contextValue = {
    brazeInitialized,
    brazeError
  }

  return <BrazeContext.Provider value={contextValue}>{children}</BrazeContext.Provider>
}
